import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLang, useT } from '../context/LangContext';
import { 
  ArrowRight, 
  Sprout, 
  Users, 
  BarChart3, 
  Eye,
  PenLine,
  TrendingUp,
  Globe,
  Loader2,
  Clock
} from 'lucide-react';
import ProgrammeContext from '../components/ProgrammeContext';
import PartnerLockup from '../components/PartnerLockup';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var countryFlags = {
  tanzania: '🇹🇿', ghana: '🇬🇭', malawi: '🇲🇼', rwanda: '🇷🇼',
  mali: '🇲🇱', 'burkina-faso': '🇧🇫', mozambique: '🇲🇿',
};

var themeLabels = {
  land: 'Land', finance: 'Finance', inputs: 'Inputs',
  markets: 'Markets', climate: 'Climate', skills: 'Skills',
};

var TICKER_MESSAGES = [
  "47 stories shared across 6 countries",
  "3 voices from Malawi just approved",
  "Akosua's story from Ghana trending now",
  "New voice from Tanzania just arrived",
  "Marie's mushroom story has 1,200 views",
  "Oumar's SMS innovation featured this week",
];

function LiveTicker() {
  var [idx, setIdx] = useState(0);
  useEffect(function() {
    var interval = setInterval(function() {
      setIdx(function(prev) { return (prev + 1) % TICKER_MESSAGES.length; });
    }, 4000);
    return function() { clearInterval(interval); };
  }, []);
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="live-ticker">
      <span className="live-dot" />
      <span className="text-[#72BF44] font-medium">Live</span>
      <span className="text-white/80 transition-all duration-500" key={idx}>{TICKER_MESSAGES[idx]}</span>
    </div>
  );
}

function StoryCard(props) {
  var story = props.story;
  var onClick = props.onClick;
  var size = props.size || 'normal';
  var flag = countryFlags[story.country_id] || '🌍';
  var themeLabel = themeLabels[story.theme] || story.theme;
  var isHero = size === 'hero';

  return (
    <div className={'story-card-alive cursor-pointer overflow-hidden rounded-2xl bg-white border border-gray-100 ' + (isHero ? 'bento-hero' : '')} onClick={onClick} data-testid={'story-card-' + story.id}>
      <div className={'relative overflow-hidden ' + (isHero ? 'h-full min-h-[360px]' : 'aspect-[4/3]')}>
        <img src={story.image_url || 'https://images.unsplash.com/photo-1551791506-775250793386?w=800'} alt={story.title} className="story-img w-full h-full object-cover" onError={(e) => { const fb = 'https://images.unsplash.com/photo-1551791506-775250793386?w=800'; if (e.currentTarget.src !== fb) e.currentTarget.src = fb; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{flag}</span>
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-0 text-xs">{themeLabel}</Badge>
            {story.views > 0 && (
              <span className="text-white/50 text-xs flex items-center gap-1 ml-auto"><Eye className="w-3 h-3" />{story.views}</span>
            )}
          </div>
          <h3 className={'font-serif text-white leading-tight ' + (isHero ? 'text-2xl md:text-3xl' : 'text-lg')}>{story.title}</h3>
          <p className="text-white/70 text-sm mt-1 line-clamp-2">{story.caption}</p>
          {story.author_name && <p className="text-white/40 text-xs mt-3">by {story.author_name}</p>}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  var navigate = useNavigate();
  var lang = useLang().lang;
  var t = useT();
  var [stories, setStories] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    setLoading(true);
    fetch(API_URL + '/api/stories/public?limit=12&lang=' + lang)
      .then(function(r) { return r.json(); })
      .then(function(d) { setStories(d); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [lang]);

  // Split stories for different sections
  var featured = stories.slice(0, 1);
  var recent = stories.slice(1, 7);
  var trending = stories.filter(function(s) { return s.views > 300; }).slice(0, 3);
  var climateStories = stories.filter(function(s) { return s.theme === 'climate'; });
  var womenStories = stories.filter(function(s) { 
    var n = (s.author_name || '').toLowerCase();
    return n.indexOf('akosua') >= 0 || n.indexOf('grace') >= 0 || n.indexOf('marie') >= 0 || n.indexOf('amina') >= 0 || n.indexOf('ama') >= 0 || n.indexOf('fatima') >= 0 || n.indexOf('mercy') >= 0 || n.indexOf('abena') >= 0 || n.indexOf('claudine') >= 0;
  });

  // Country counts
  var countryCounts = {};
  stories.forEach(function(s) {
    countryCounts[s.country_id] = (countryCounts[s.country_id] || 0) + 1;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F8F6' }}>

      {/* ===== HERO — editorial composition: ticker → about ribbon → headline ===== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="/images/home-hero.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" style={{ objectPosition: '50% 20%' }} />
          {/* Primary green tone-press */}
          <div className="absolute inset-0 bg-[#07743F]/85" />
          {/* Top-to-bottom legibility ramp + asymmetric reveal on the right */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#062E1B]/55 via-transparent to-[#07743F]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07743F]/40 via-transparent to-[#07743F]/0" />
        </div>

        <div className="container mx-auto px-4 pt-16 md:pt-20 pb-24 md:pb-32 relative z-10">
          <div className="max-w-6xl">

            {/* 1 · LIVE TICKER */}
            <div data-testid="hero-ticker-slot">
              <LiveTicker />
            </div>

            {/* 2 · ABOUT YEFFA — editorial ribbon between ticker and headline */}
            <div
              className="mt-10 md:mt-12 pt-8 md:pt-10 pb-10 md:pb-12 border-t border-white/15"
              data-testid="about-yeffa-hero"
              aria-labelledby="about-yeffa-hero-heading"
            >
              <div className="grid grid-cols-12 gap-x-8 gap-y-4">
                <div className="col-span-12 md:col-span-3">
                  <p
                    id="about-yeffa-hero-heading"
                    className="kicker text-[#E4E517]"
                  >
                    {t('about_yeffa.kicker', 'About YEFFA')}
                  </p>
                  <p className="kicker text-white/40 mt-2">
                    {t('about_yeffa.subkicker', 'Programme context')}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-9 md:border-l md:border-white/10 md:pl-8">
                  <p className="display-sub text-white text-xl md:text-2xl lg:text-[26px] leading-snug max-w-[58ch]">
                    <span className="text-[#E4E517]">
                      {t('about_yeffa.lead', 'Youth Entrepreneurship for the Future of Food and Agriculture (YEFFA)')}
                    </span>{' '}
                    <span className="text-white/90">
                      {t('about_yeffa.body_short', 'is a five-year initiative led by AGRA in partnership with Mastercard Foundation — enabling young women and men across Africa to build dignified, fulfilling work in food systems.')}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* 3 · HERO HEADLINE */}
            <div className="pt-10 md:pt-14 border-t border-white/15 max-w-3xl" data-testid="hero-headline-block">
              <h1 className="display-hero text-5xl sm:text-6xl lg:text-7xl text-white mb-8" data-testid="hero-title">
                {t('home.hero.line1', 'Grow Your')}{' '}<span className="italic-accent">{t('home.hero.tomorrow', 'Tomorrow.')}</span><br />
                <span className="text-[#E4E517]">{t('home.hero.line2', 'Start with your story.')}</span>
              </h1>
              <p className="body-prose text-lg md:text-xl text-white/85 mb-10">
                {t('home.hero.intro', 'Real stories. Real futures. Built by young people across 6 African countries.')}{' '}
                <strong className="text-white">{stories.length} {t('home.hero.voices_counting', 'voices and counting.')}</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={function() { navigate('/submit'); }} className="cta-alive bg-[#E4E517] text-[#07743F] hover:bg-[#d4d514] px-8 py-6 text-lg font-semibold rounded-full" data-testid="hero-cta-share">
                  <PenLine className="w-5 h-5 mr-2" />{t('cta.tell_your_story', 'Tell Your Story')}
                </Button>
                <Button onClick={function() { navigate('/stories'); }} variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full" data-testid="hero-cta-explore">
                  {t('home.hero.explore', 'Explore Voices')}<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <p className="text-white/40 text-sm mt-6">{t('home.hero.note', 'Takes less than 2 minutes. Your story matters.')}</p>
            </div>

          </div>
        </div>
      </section>

      {/* ===== FEATURED STORY ===== */}
      {featured.length > 0 && (
        <section className="py-16 bg-white" data-testid="featured-section">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-[#E4E517] rounded-full" />
                <div>
                  <p className="kicker text-[#72BF44]">{t('home.featured.eyebrow', 'Featured story')}</p>
                  <h2 className="display-sub text-3xl md:text-4xl text-gray-900">{t('home.featured.title', 'Trending this week')}</h2>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img src={featured[0].image_url} alt={featured[0].title} className="w-full h-full object-cover story-img" onError={(e) => { const fb = 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800'; if (e.currentTarget.src !== fb) e.currentTarget.src = fb; }} />
                </div>
                <div className="py-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">{countryFlags[featured[0].country_id]}</span>
                    <Badge className="bg-[#07743F]/10 text-[#07743F] border-0">{featured[0].country_name}</Badge>
                    <Badge className="bg-[#E4E517]/20 text-[#07743F] border-0">{themeLabels[featured[0].theme]}</Badge>
                    {featured[0].views > 0 && <span className="text-gray-400 text-sm flex items-center gap-1 ml-auto"><Eye className="w-4 h-4" />{featured[0].views} views</span>}
                  </div>
                  <h3 className="display-sub text-3xl text-gray-900 mb-3">{featured[0].title}</h3>
                  <p className="text-gray-500 text-lg leading-relaxed mb-4">{featured[0].caption}</p>
                  <p className="text-gray-400 text-sm mb-6">{t('common.by', 'by')} {featured[0].author_name} — {featured[0].country_name}</p>
                  <Button onClick={function() { navigate('/story/' + featured[0].id); }} className="bg-[#07743F] hover:bg-[#065a32] text-white rounded-full px-6" data-testid="featured-read-more">
                    {t('home.featured.read_full', 'Read full story')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== RECENT VOICES ===== */}
      <section className="py-16" data-testid="stories-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="kicker text-[#72BF44] mb-2">{t('home.recent.eyebrow', 'Latest voices')}</p>
                <h2 className="display-sub text-3xl md:text-4xl text-gray-900">{t('home.recent.title', 'Stories that move')}</h2>
              </div>
              <Button onClick={function() { navigate('/stories'); }} variant="ghost" className="text-[#07743F] hover:bg-green-50 hidden sm:flex" data-testid="view-all-stories">
                {t('home.recent.see_all', 'See all')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#07743F]" /></div>
            ) : (
              <div className="bento-grid">
                {recent.map(function(story, index) {
                  return (
                    <div key={story.id} className="animate-discover" style={{ animationDelay: index * 0.08 + 's' }}>
                      <StoryCard story={story} size={index === 0 ? 'hero' : 'normal'} onClick={function() { navigate('/story/' + story.id); }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== TRENDING THEMES ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <p className="kicker text-[#72BF44] mb-2">Trending themes</p>
            <h2 className="display-sub text-3xl md:text-4xl text-gray-900 mb-8">What young farmers are talking about</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { id: 'climate', label: 'Climate Innovation', count: climateStories.length, color: 'from-emerald-500 to-teal-600' },
                { id: 'markets', label: 'Digital Agriculture', count: stories.filter(function(s){return s.theme==='markets';}).length, color: 'from-blue-500 to-indigo-600' },
                { id: 'finance', label: 'Youth Enterprise', count: stories.filter(function(s){return s.theme==='finance';}).length, color: 'from-amber-500 to-orange-600' },
                { id: 'inputs', label: 'Urban Farming', count: stories.filter(function(s){return s.theme==='inputs';}).length, color: 'from-purple-500 to-pink-600' },
              ].map(function(theme) {
                return (
                  <button key={theme.id} onClick={function() { navigate('/stories?theme=' + theme.id); }} className="text-left p-5 rounded-2xl bg-gradient-to-br text-white group hover:shadow-lg transition-all" style={{ background: 'linear-gradient(135deg, var(--tw-gradient-from), var(--tw-gradient-to))' }}>
                    <div className={'p-5 rounded-2xl text-white group hover:shadow-lg transition-all bg-gradient-to-br ' + theme.color}>
                      <TrendingUp className="w-5 h-5 mb-3 opacity-80" />
                      <p className="font-semibold text-sm">{theme.label}</p>
                      <p className="text-white/60 text-xs mt-1">{theme.count} stories</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== WOMEN IN AGRICULTURE ===== */}
      {womenStories.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-[#72BF44] rounded-full" />
                <div>
                  <p className="kicker text-[#72BF44]">Spotlight</p>
                  <h2 className="display-sub text-2xl md:text-3xl text-gray-900">Women in Agriculture</h2>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {womenStories.slice(0, 3).map(function(story, idx) {
                  return (
                    <div key={story.id} className="animate-discover" style={{ animationDelay: idx * 0.1 + 's' }}>
                      <StoryCard story={story} onClick={function() { navigate('/story/' + story.id); }} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== COUNTRY ACTIVITY ===== */}
      <section className="py-16 bg-[#07743F] relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/journey-ducks.jpg" alt="" className="w-full h-full object-cover opacity-15" aria-hidden="true" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <p className="kicker text-[#E4E517] mb-3 text-center">Across the continent</p>
            <h2 className="display-section text-3xl sm:text-4xl md:text-5xl text-white text-center mb-10">6 countries. {stories.length} voices. One movement.</h2>
            <div className="grid md:grid-cols-3 gap-8 text-center text-white mb-10">
              <div><p className="text-5xl font-serif font-bold text-[#E4E517] mb-2">6</p><p className="text-white/70 text-sm">Countries active</p></div>
              <div><p className="text-5xl font-serif font-bold text-[#E4E517] mb-2">{stories.length}+</p><p className="text-white/70 text-sm">Stories published</p></div>
              <div><p className="text-5xl font-serif font-bold text-[#E4E517] mb-2">5</p><p className="text-white/70 text-sm">Distribution channels</p></div>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { id: 'tanzania', name: 'Tanzania', flag: '🇹🇿' },
                { id: 'ghana', name: 'Ghana', flag: '🇬🇭' },
                { id: 'malawi', name: 'Malawi', flag: '🇲🇼' },
                { id: 'rwanda', name: 'Rwanda', flag: '🇷🇼' },
                { id: 'mali', name: 'Mali', flag: '🇲🇱' },
                { id: 'burkina-faso', name: 'Burkina Faso', flag: '🇧🇫' },
              ].map(function(country) {
                var count = countryCounts[country.id] || 0;
                return (
                  <button key={country.id} onClick={function() { navigate('/stories?country=' + country.id); }} className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 transition-all group">
                    <span className="text-2xl group-hover:scale-110 transition-transform">{country.flag}</span>
                    <span className="font-medium text-white text-sm">{country.name}</span>
                    {count > 0 && <Badge className="bg-[#E4E517] text-[#07743F] border-0 text-xs">{count}</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24" data-testid="cta-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#07743F] rounded-3xl overflow-hidden relative">
              <div className="grid md:grid-cols-2">
                <div className="relative min-h-[300px] md:min-h-[400px] overflow-hidden hidden md:block">
                  <img src="/images/home-field.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#07743F]/40" />
                </div>
                <div className="p-10 md:p-14 flex flex-col justify-center text-center md:text-left">
                  <h2 className="display-section text-3xl sm:text-4xl md:text-5xl text-white mb-6">
                    Your story is the evidence <span className="italic text-[#E4E517]">they need to hear.</span>
                  </h2>
                  <p className="text-green-200 mb-8 leading-relaxed">Every voice shared becomes part of something bigger. Policy. Investment. Change.</p>
                  <div>
                    <Button onClick={function() { navigate('/submit'); }} className="cta-alive bg-[#E4E517] text-[#07743F] hover:bg-[#d4d514] px-10 py-7 text-lg font-semibold rounded-full" data-testid="cta-tell-story">
                      <PenLine className="w-5 h-5 mr-2" />Tell Your Story
                    </Button>
                  </div>
                  <p className="text-white/40 text-sm mt-4">No account needed. Takes less than 2 minutes.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROGRAMME CONTEXT (About GYT, partner lock-up, audio feature) ===== */}
      <ProgrammeContext />

      {/* ===== FOOTER ===== */}
      <footer className="bg-gray-900 text-white py-14" data-testid="site-footer">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10 items-start">

            {/* Programme block */}
            <div className="md:col-span-2" data-testid="footer-programme">
              <h3 className="display-sub text-xl md:text-2xl text-white mb-3">
                {t('footer.yeffa_full', 'Youth Entrepreneurship for the Future of Food and Agriculture (YEFFA)')}
              </h3>
              <p className="text-[14px] text-gray-300 leading-relaxed max-w-2xl italic">
                {t('footer.gyt_blurb', "Grow Your Tomorrow is YEFFA's storytelling platform, designed to capture and amplify the voices of young people transforming Africa's food systems.")}
              </p>
              <div className="mt-6" data-testid="footer-partner-lockup">
                <PartnerLockup variant="dark" size="md" />
              </div>
            </div>

            {/* Links */}
            <div className="flex md:justify-end" data-testid="footer-links">
              <div className="flex flex-col gap-3 text-sm text-gray-400">
                <button onClick={function() { navigate('/stories'); }} className="hover:text-white transition-colors text-left">{t('nav.stories', 'Stories')}</button>
                <button onClick={function() { navigate('/how'); }} className="hover:text-white transition-colors text-left">{t('nav.journey', 'The Journey')}</button>
                <button onClick={function() { navigate('/submit'); }} className="hover:text-white transition-colors text-left">{t('footer.share', 'Share')}</button>
                <button onClick={function() { navigate('/login'); }} className="hover:text-white transition-colors text-left">{t('nav.sign_in', 'Sign In')}</button>
              </div>
            </div>

          </div>
        </div>
      </footer>
    </div>
  );
}
